import React, {
  useCallback,
} from 'react';
import {
  selection,
} from 'redux-resource-plugins';
import {
  Droppable,
  DragDropContext,
} from 'react-beautiful-dnd';
import {
  getStatus,
} from 'redux-resource';
import i18n from 'i18next';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreUtils,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  ID,
  arrayMove,
  updateItemPositonOnList,
} from 'web-utils';

import {
  uiActions,
} from 'web-actions';

import {
  Spinner,
  WorkoutExercise,
} from 'web-components';

import {
  Flex,
} from 'web-styled';

import * as S from './styled';

type Props = {
  type: string;
  readOnly: boolean;
  workoutId: number;
  workoutType: string;
};

const WorkoutExercisesList = ({
  type,
  readOnly,
  workoutId,
  workoutType,
}: Props) => {
  const resourceType = 'workoutExercises';
  const mapState = useCallback(state => ({
    updateStatus: getStatus(
      state,
      [
        'workoutExercises.requests.addExercise.status',
        'workoutExercises.requests.updateOrder.status',
        'workoutExercises.requests.setOrderName.status',
        'workoutExercises.requests.addAlternative.status',
        'exerciseProperties.requests.deleteEntity.status',
        'exerciseProperties.requests.assignProperties.status',
        'exerciseProperties.requests.saveExerciseProperty.status',
      ],
      false,
    ),
    exercises: coreSelectors.getSortedListWorkoutExercises(
      workoutId,
      true,
    )(state),
    visibleSelect: coreSelectors.getWorkoutProperties(
      workoutId,
    )(state).length > 0,
    defaultProperty: coreSelectors.getDefaultProperty(state),
  }), [workoutId]);

  const {
    exercises,
    updateStatus,
    visibleSelect,
    defaultProperty,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const numbers = exercises.map(item => item.number);

  const handleSelect = (exerciseId, selected) => {
    dispatch(selected
      ? selection.selectResources(resourceType, [exerciseId])
      : selection.deselectResources(resourceType, [exerciseId]));
  };

  const handleUpdate = (exerciseId, payload) => {
    dispatch(
      coreActions.updateResources({
        resourceType,
        resources: [{
          id: exerciseId,
          ...payload,
        }],
      }),
    );
  };

  const handleRequestUpdate = (exerciseId, payload) => {
    dispatch(
      coreActions.updateResources({
        resourceType,
        resources: [{
          id: exerciseId,
          ...payload,
        }],
      }),
    );
    dispatch(
      coreActions.requestUpdateEntity({
        id: exerciseId,
        payload,
        resourceType,
      }),
    );
  };

  const handleDelete = (exercise) => {
    const onSuccess = () => {
      const resources = coreUtils.updateOrderNameExercises(
        exercises.filter(e => e.id !== exercise.id),
      );
      dispatch(
        coreActions.updateResources({
          resourceType,
          resources,
        }),
      );

      dispatch(
        coreActions.requestUpdateItems({
          resources,
          resourceType,
        }),
      );
    };

    const onConfirmAction = coreActions.requestDeleteItems({
      resourceType,
      resources: [exercise, ...exercise.alternatives].map(e => e.id),
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete Exercise',
          bodyText: `Are you sure you want to delete <b>${exercise.name}</b>?`,
        },
        onSuccess,
        onConfirmAction,
      }),
    );
  };

  const handleReplace = ({ id }) => {
    const onSelect = (newExerciseId) => {
      dispatch(
        coreActions.requestReplaceExerciseInWorkout({
          workoutId,
          exerciseId: id,
          newExerciseId,
          workoutType,
        }),
      );
    };

    dispatch(
      uiActions.openExercisePicker({
        onSelect,
      }),
    );
  };

  const handleSetOrderName = (exerciseId, newNumber) => {
    dispatch(
      coreActions.requestSetExerciseOrderName({
        workoutId,
        exerciseId,
        newNumber,
      }),
    );
  };

  const handleAddProperty = (payload) => {
    dispatch(
      coreActions.addResources({
        resourceType: 'exerciseProperties',
        resources: [{
          id: ID(),
          isNew: true,
          ...payload,
          ...defaultProperty,
        }],
        list: `workout_${workoutId}`,
      }),
    );
  };

  const handleSaveProperty = (propertyId, payload, isNew) => {
    dispatch(
      coreActions.requestSaveExerciseProperty(
        propertyId,
        payload,
        isNew,
        `workout_${workoutId}`,
      ),
    );
  };

  const handleDeleteProperty = (propertyId, isNew) => {
    dispatch(isNew
      ? coreActions.deleteResources({
        resourceType: 'exerciseProperties',
        resources: [{
          id: propertyId,
        }],
      })
      : coreActions.requestDeleteEntity({
        id: propertyId,
        resourceType: 'exerciseProperties',
      }));
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    // same position
    if (source.index === destination.index) {
      return;
    }

    if (result.type === 'property') {
      const exerciseId = +destination.droppableId;
      const list = Array.from(
        exercises.find(e => e.id === exerciseId).properties,
      );
      const resources = updateItemPositonOnList(
        arrayMove(
          list,
          source.index,
          destination.index,
        ),
      );

      dispatch(
        coreActions.updateResources({
          resourceType: 'exerciseProperties',
          resources,
        }),
      );

      dispatch(
        coreActions.requestUpdateItems({
          resources,
          resourceType: 'exerciseProperties',
        }),
      );
    } else {
      const resources = coreUtils.updateOrderNameExercises(
        arrayMove(
          exercises,
          source.index,
          destination.index,
        ),
      );

      dispatch(
        coreActions.updateResources({
          resourceType,
          resources,
        }),
      );

      dispatch(
        coreActions.requestUpdateItems({
          resources,
          resourceType,
        }),
      );
    }
  };

  const handleAddAlternative = (exerciseId) => {
    const onSelect = (alternativeId: number) => {
      dispatch(
        coreActions.requestAddAlternativeExercise({
          workoutId,
          exerciseId,
          alternativeId,
          workoutType,
        }),
      );
    };

    dispatch(
      uiActions.openExercisePicker({
        onSelect,
      }),
    );
  };

  const handleSetAlternative = (exerciseId, alternativeId) => {
    dispatch(
      coreActions.requestSetAlternativeExercise({
        exerciseId,
        alternativeId,
      }),
    );
  };

  const handleRemoveAlternative = (alternative) => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id: alternative.id,
      resourceType,
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete Alternative?',
          bodyText: `Are you sure you want to delete alternative <b>${alternative.name}</b>?`,
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  };

  if (exercises.length === 0) {
    return (
      <S.Empty>
        {i18n.t('there.no.exercises', { type: `$t(${type})` })}
      </S.Empty>
    );
  }

  const allExercises = exercises.map((exercise, index) => (
    <WorkoutExercise
      key={exercise.id}
      index={index}
      readOnly={readOnly}
      exercise={exercise}
      number={numbers[index]}
      numbers={numbers}
      prevNumber={numbers[index - 1]}
      nextNumber={numbers[index + 1]}
      properties={exercise.properties}
      visibleSelect={visibleSelect}
      onSelect={handleSelect}
      onUpdate={handleUpdate}
      onRequestUpdate={handleRequestUpdate}
      onRequestDelete={handleDelete}
      onRequestReplace={handleReplace}
      onRequestSetOrderName={handleSetOrderName}
      onRequestAddProperty={handleAddProperty}
      onRequestSaveProperty={handleSaveProperty}
      onRequestDeleteProperty={handleDeleteProperty}
      onRequestAddAlternative={handleAddAlternative}
      onRequestSetAlternative={handleSetAlternative}
      onRequestRemoveAlternative={handleRemoveAlternative}
    />
  ));

  return (
    <Flex fillSpace>
      {readOnly
        ? (
          <Flex
            width="100%"
            column
          >
            {allExercises}
          </Flex>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable
              type="exercise"
              droppableId="workout"
            >
              {droppableProvided => (
                <Flex
                  ref={droppableProvided.innerRef}
                  width="100%"
                  column
                >
                  {allExercises}
                  {droppableProvided.placeholder}
                </Flex>
              )}
            </Droppable>
          </DragDropContext>
        )}
      {updateStatus.pending && (
        <Spinner over />
      )}
    </Flex>
  );
};

export default WorkoutExercisesList;
